import Immutable from 'immutable';
import { ExportSortDirection } from 'records/Export';
import { NewSubmittal } from 'records/NewSubmittal';
import { ProjectId } from 'records/Project';
import {
  RawSubmittal,
  Submittal,
  SubmittalId,
  SubmittalList,
  SubmittalListTypes,
} from 'records/Submittal';
import {
  RawSubmittalCategory,
  SubmittalCategory,
  SubmittalCategoryList,
} from 'records/SubmittalCategory';
import {
  RawSubmittalReviewerResponse,
  SubmittalReviewerResponse,
  SubmittalReviewerResponseList,
} from 'records/SubmittalReviewerResponse';
import {
  RawSubmittalStatus,
  SubmittalStatus,
  SubmittalStatusList,
} from 'records/SubmittalStatus';
import { UploadData } from 'records/UploadData';
import GQLRequest from 'utils/GQLRequest';
import { v4 as uuidv4 } from 'uuid';
import { beginUpload } from './fileService';

export interface SubmittalResponse {
  submittals: RawSubmittal[];
  cursor: string;
}

export interface SubmittalSpecInfo {
  specnumber: string;
  specname: string;
}

export interface UploadResponseItem {
  documentid: string;
  signedurl: string;
  filename: string;
  filesize: number;
}

export type UploadFileType = 'DRAWING' | 'SUBMITTAL';

const submittalSchema = `
  id
  projectid
  number
  specsectionnumber
  specsectionname
  sectioncount
  category {
    kind
    display
  }
  description
  status {
    kind
    display
  }
  ballincourt
  responsiblecompany
  duefromsub
  duefromreviewer
  leadtime
  requiredonsite
  dateapproved
  datecreated
  createdby
  attachments
  reviewerresponse{
    kind
    display
  }
  subrespondedby
  subrespondedon
  reviewedby
  reviewedon
  subassignedby
  subassignedon
  reviewerassignedy
  reviewerassignedon
  attachmentfilename
`;

export const getSubmittalTemplateURL = async (): Promise<string> => {
  const { getSubmittalTemplateUrl } = await new GQLRequest()
    .query(
      `
      query GetSubmittalTemplateUrl {
        getSubmittalTemplateUrl
      }
      `
    )
    .json<{ getSubmittalTemplateUrl: string }>();

  return getSubmittalTemplateUrl;
};

export const getSubmittalStatuses = async (): Promise<SubmittalStatusList> => {
  const resp = await new GQLRequest()
    .query(
      `
      query GetSubmittalStatuses {
        getSubmittalStatuses {
          kind
          display
        }
      }
      `
    )
    .json<{ getSubmittalStatuses: RawSubmittalStatus[] }>();

  return Immutable.List(
    resp.getSubmittalStatuses.map((rs) => new SubmittalStatus(rs))
  );
};

export const getSubmittalReviewerResponses =
  async (): Promise<SubmittalReviewerResponseList> => {
    const resp = await new GQLRequest()
      .query(
        `
      query GetSubmittalReviewerResponses {
        getSubmittalReviewerResponses {
          kind
          display
        }
      }
      `
      )
      .json<{
        getSubmittalReviewerResponses: RawSubmittalReviewerResponse[];
      }>();

    return Immutable.List(
      resp.getSubmittalReviewerResponses.map(
        (rs) => new SubmittalReviewerResponse(rs)
      )
    );
  };

export const getSubmittalCategories =
  async (): Promise<SubmittalCategoryList> => {
    const resp = await new GQLRequest()
      .query(
        `
      query GetSubmittalCategories {
        getSubmittalCategories {
          kind
          display
        }
      }
      `
      )
      .json<{ getSubmittalCategories: RawSubmittalCategory[] }>();

    return Immutable.List(
      resp.getSubmittalCategories.map((rs) => new SubmittalCategory(rs))
    );
  };

export async function getNextSubmittals(
  cursor: string
): Promise<SubmittalResponse> {
  const resp = await new GQLRequest()
    .query(
      `
      query GetNextSubmittals($cursor: String!) {
        getNextSubmittals(cursor: $cursor) {
          submittals {
            ${submittalSchema}
          }
          cursor
        }
      }
      `
    )
    .variables({ cursor })
    .json<{ getNextSubmittals: SubmittalResponse }>();

  return resp.getNextSubmittals;
}

export async function getSubmittal(
  id: SubmittalId,
  projectid: ProjectId
): Promise<Submittal> {
  const resp = await new GQLRequest()
    .query(
      `
      query getSubmittal($id: String!, $projectid: String!) {
        getSubmittal(id: $id, projectid: $projectid) {
          ${submittalSchema}
        }
      }
      `
    )
    .variables({ id, projectid })
    .json<{ getSubmittal: RawSubmittal }>();

  return Submittal.deserialize(resp.getSubmittal);
}

export async function getSubmittals(
  projectid: ProjectId,
  listType: SubmittalListTypes,
  pageSize = 1000
): Promise<SubmittalList> {
  const resp = await new GQLRequest()
    .query(
      `
      query GetSubmittals($projectid: String!, $listType: SubmittalListType!, $pageSize: Int!) {
        getSubmittals(projectid: $projectid, listType: $listType, pageSize: $pageSize) {
          submittals {
            ${submittalSchema}
          }
          cursor
        }
      }
      `
    )
    .variables({ projectid, listType, pageSize })
    .json<{ getSubmittals: SubmittalResponse }>();

  let submittalsList = Immutable.List<Submittal>(
    resp.getSubmittals.submittals.map(Submittal.deserialize)
  );
  let { cursor } = resp.getSubmittals;

  while (cursor) {
    const { submittals: nextRawSubmittals, cursor: nextCursor } =
      await getNextSubmittals(cursor);

    const nextSubmittals = nextRawSubmittals.map(Submittal.deserialize);

    submittalsList = submittalsList.concat(nextSubmittals);
    cursor = nextCursor;
  }

  return submittalsList;
}

export async function createSubmittal(
  newSubmittal: NewSubmittal
): Promise<Submittal> {
  // make sure that the spec section number is of the format "00 00 00", WITH spaces.
  const newSubWithSpacedSpecNumber = newSubmittal.update(
    'specsectionnumber',
    (ssn) => {
      return ssn.indexOf(' ') === -1
        ? ssn
            .split('')
            .reduce(
              (acc, v, i) => `${acc}${i > 0 && i % 2 === 0 ? ' ' : ''}${v}`,
              ''
            )
        : ssn;
    }
  );

  const resp = await new GQLRequest()
    .query(
      `
    mutation createSubmittal($input: NewSubmittal!) {
      createSubmittal(input: $input) {
        ${submittalSchema}
      }
    }
    `
    )
    .variables({ input: newSubWithSpacedSpecNumber.serialize() })
    .json<{ createSubmittal: RawSubmittal }>();

  return Submittal.deserialize(resp.createSubmittal);
}

export async function updateSubmittal(
  submittal: Submittal,
  files: Immutable.List<File>
): Promise<Submittal> {
  const resp = await new GQLRequest()
    .query(
      `
      mutation UpdateSubmittal($input: UpdateSubmittal!) {
        updateSubmittal(input: $input) {
          ${submittalSchema}
        }
      }
      `
    )
    .variables({ input: submittal.toUpdateSubmittal() })
    .json<{ updateSubmittal: RawSubmittal }>();

  // Upload files
  if (files && files.count() > 0) {
    const newSub = await uploadSubmittalDocument(
      submittal.projectid,
      files.first(),
      submittal.id
    );
    return newSub || Submittal.deserialize(resp.updateSubmittal);
  }

  return Submittal.deserialize(resp.updateSubmittal);
}

export async function updateSubmittalNoFile(
  submittal: Submittal
): Promise<Submittal> {
  return updateSubmittal(submittal, Immutable.List<File>());
}

export async function designSubmittalResponse(
  submittal: Submittal,
  doc: File
): Promise<Submittal> {
  let docupload;

  if (doc) {
    docupload = await beginUploadSubmittalDocument(submittal.projectid, doc);
  }

  const filename = doc ? doc.name : undefined;
  const docid = docupload?.documentid ? docupload?.documentid : undefined;

  const resp = await new GQLRequest()
    .query(
      `
      mutation DesignSubmittalResponse($input: SubmittalDesignResponse!) {
        designSubmittalResponse(input: $input) {
          ${submittalSchema}
        }
      }
      `
    )
    .variables({
      input: {
        id: submittal.id,
        projectid: submittal.projectid,
        documentid: docid,
        filename,
        reviewerresponse: submittal.reviewerresponse.kind,
      },
    })
    .json<{ designSubmittalResponse: RawSubmittal }>();

  return Submittal.deserialize(resp.designSubmittalResponse);
}

export async function respondToSubmittal(
  submittal: Submittal,
  doc: File | undefined
): Promise<Submittal> {
  let docupload;

  if (doc) {
    docupload = await beginUploadSubmittalDocument(submittal.projectid, doc);
  }

  const filename = doc ? doc.name : undefined;
  const docid = docupload?.documentid ? docupload?.documentid : undefined;

  const resp = await new GQLRequest()
    .query(
      `
      mutation RespondToSubmittal($input: SubmittalResponse!) {
        respondToSubmittal(input: $input) {
          ${submittalSchema}
        }
      }
      `
    )
    .variables({
      input: {
        id: submittal.id,
        projectid: submittal.projectid,
        documentid: docid,
        filename,
      },
    })
    .json<{ respondToSubmittal: RawSubmittal }>();

  return Submittal.deserialize(resp.respondToSubmittal);
}

export const beginUploadSubmittalDocument = async (
  projectid: string,
  doc: File
): Promise<UploadResponseItem | undefined> => {
  try {
    const uploadType = 'SUBMITTAL';
    const {
      beginDocumentUpload: { item },
    } = await new GQLRequest()
      .query(
        `
          mutation BeginDocUpload($projectid: String!, $item: UploadRequestItem!, $uploadType: DocumentUploadType) {
            beginDocumentUpload(projectid: $projectid, item: $item, uploadType: $uploadType) {              
              projectid
              processrequestid
              item {
                documentid
                signedurl
              }
            }
          }
          `
      )
      .variables({
        projectid,
        item: {
          filename: doc.name,
          filesize: doc.size,
          clientGeneratedID: uuidv4(),
        },
        uploadType,
      })
      .json<{
        beginDocumentUpload: { item: UploadResponseItem };
      }>();

    const resp = await fetch(item.signedurl, {
      method: 'PUT',
      body: doc,
    });

    return resp ? item : undefined;
  } catch (e) {
    console.log('uploadDocument err:', e);
  }
  return undefined;
};

export const uploadSubmittalDocument = async (
  projectid: string,
  doc: File,
  submittalid: string
): Promise<Submittal | undefined> => {
  try {
    const { name } = doc;
    const item = await beginUploadSubmittalDocument(projectid, doc);

    if (item) {
      await new GQLRequest()
        .query(
          `
          mutation CompleteSubmittalUpload($documentid: String!, $projectid: String!, $submittalid: String!, $name: String!) {
            completeSubmittalUpload(input: {documentid: $documentid, projectid: $projectid, submittalid: $submittalid, name: $name}){
              submittalid
              documentid
            }
          }
          `
        )
        .variables({
          documentid: item.documentid,
          projectid,
          submittalid,
          name,
        })
        .json<{ completeDocumentUpload: boolean }>();

      const updatedSubmittal = await getSubmittal(submittalid, projectid);

      return updatedSubmittal;
    }
  } catch (e) {
    console.log('uploadDocument err:', e);
  }
  return undefined;
};

export async function deleteSubmittal(submittal: Submittal): Promise<boolean> {
  const resp = await new GQLRequest()
    .query(
      `
      mutation DeleteSubmittal($input: DeleteSubmittal!) {
        deleteSubmittal(input: $input)
      }
      `
    )
    .variables({ input: { projectid: submittal.projectid, id: submittal.id } })
    .json<{ deleteSubmittal: boolean }>();

  return resp.deleteSubmittal;
}

export async function completeSubmittalLogUpload(
  projectid: ProjectId,
  fileid: string
): Promise<boolean> {
  const now = new Date();
  const utcoffset = now.getTimezoneOffset() * 60;
  const resp = await new GQLRequest()
    .query(
      `
      mutation CompleteSubmittalUpload($projectid: String!, $fileid: String!, $utcoffset: Int!) {
        completeSubmittalLogUpload(projectid: $projectid, fileid: $fileid, utcoffset: $utcoffset)
      }
      `
    )
    .variables({ projectid, fileid, utcoffset })
    .json<{ completeSubmittalLogUpload: boolean }>();

  return resp.completeSubmittalLogUpload;
}

export async function uploadSubmittalLog(
  projectid: ProjectId,
  log: File
): Promise<boolean> {
  const uploadData = new UploadData({
    projectid,
    filename: log.name,
    category: 'Submittal Log',
    extension: `.${log.name.split('.').pop()}`,
    filesize: log.size,
    file: log,
  });

  const { signedurl, fileid } = await beginUpload(uploadData);

  const uploadResp = await fetch(signedurl, {
    method: 'PUT',
    body: log,
  });

  if (uploadResp.ok) {
    const uploadComplete = await completeSubmittalLogUpload(projectid, fileid);

    return uploadComplete;
  }

  return false;
}

export async function getSubmittalSpecInfo(
  specnumber: string
): Promise<SubmittalSpecInfo> {
  const resp = await new GQLRequest()
    .query(
      `
      query GetSubmittalSpecInfo($specnumber: String!) {
        getSubmittalSpecInfo(specnumber: $specnumber) {
          specnumber
          specname
        }
      }
      `
    )
    .variables({ specnumber })
    .json<{ getSubmittalSpecInfo: SubmittalSpecInfo }>();

  return resp.getSubmittalSpecInfo;
}

export const exportXLSXSubmittals = async (
  projectid: string,
  listType: SubmittalListTypes,
  ids: string[],
  sortfield: string,
  sortdirection: ExportSortDirection
): Promise<{ url: string; extension: string }> => {
  const now = new Date();
  const utcoffset = now.getTimezoneOffset() * 60;
  const r = await new GQLRequest()
    .query(
      `
      query ExportXLSXSubmittals($projectid: String!, $listType: SubmittalListType!, $utcoffset: Int!, $ids: [String!], $sortfield: String, $sortdirection: ExportSortDirection) {
        exportSubmittalLog(projectid: $projectid, listType: $listType, utcoffset: $utcoffset, ids: $ids, sortfield: $sortfield, sortdirection: $sortdirection) {
          url
          extension
        }
      }
      `
    )
    .variables({
      projectid,
      listType,
      utcoffset,
      ids,
      sortfield,
      sortdirection,
    })
    .json<{ exportSubmittalLog: { url: string; extension: string } }>();

  return r.exportSubmittalLog;
};

export const downloadSubmittalAttachment = async (
  projectid: string,
  documentid: string
): Promise<string> => {
  const result = await new GQLRequest()
    .query(
      `
      query DownloadSubmittalAttachment($projectid: String!, $documentid: String!) {
        downloadSubmittalAttachment(projectid: $projectid, documentid: $documentid) {
          url
        }
      }
      `
    )
    .variables({ projectid, documentid })
    .json<{ downloadSubmittalAttachment: { url: string } }>();

  return result.downloadSubmittalAttachment.url;
};
